import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api, { mediaUrl } from "@/lib/api";
import Reveal from "@/components/shared/Reveal";
import { featuredEdition } from "@/lib/useEditions";
import { useSettings } from "@/components/layout/PublicLayout";
import Seo from "@/components/shared/Seo";
import Btn from "@/components/shared/Btn";
import { ArrowRight, ExternalLink } from "lucide-react";

const PartnerCard = ({ p }) => (
  <div className={`group border p-8 flex flex-col h-[380px] ${p.level === "premium" ? "border-f2b-gold bg-f2b-black text-white" : "border-f2b-filet bg-white"}`} data-testid={`partner-${p.name}`}>
    <div className="h-16 flex items-center justify-start mb-6">
      {mediaUrl(p.logo) ? (
        <img src={mediaUrl(p.logo)} alt={p.name} className="max-h-14 max-w-full object-contain object-left" />
      ) : (
        <span className={`font-display font-bold text-2xl ${p.level === "premium" ? "text-white" : "text-f2b-black"}`}>{p.name}</span>
      )}
    </div>
    <span className={`f2b-kicker mb-2 ${p.level === "premium" ? "text-f2b-gold" : p.level === "media" ? "text-f2b-gray" : "text-f2b-cyan"}`}>
      {p.level === "premium" ? "Partenaire premium" : p.level === "media" ? "Média partenaire" : "Partenaire"}
    </span>
    <h3 className={`font-display font-bold text-xl ${p.level === "premium" ? "text-white" : "text-f2b-black"}`}>{p.name}</h3>
    <p className={`text-sm mt-3 flex-1 leading-relaxed line-clamp-3 ${p.level === "premium" ? "text-white/70" : "text-f2b-text/70"}`}>{p.description}</p>
    {p.website && (
      <a href={p.website} target="_blank" rel="noreferrer" className={`inline-flex items-center gap-2 mt-5 text-sm f2b-link-underline ${p.level === "premium" ? "text-f2b-cyan" : "text-f2b-black"}`}>
        Site web <ExternalLink size={14} />
      </a>
    )}
  </div>
);

const Partenaires = () => {
  const settings = useSettings();
  const tx = settings?.pages?.partenaires || {};
  const { data: editions = [] } = useQuery({ queryKey: ["editions"], queryFn: async () => (await api.get("/editions")).data });
  const featured = featuredEdition(editions);
  const { data: partners = [], isLoading } = useQuery({
    queryKey: ["partners", featured?.id],
    queryFn: async () => (await api.get(`/partners?edition_id=${featured.id}`)).data,
    enabled: !!featured?.id,
  });
  const premium = partners.filter((p) => p.level === "premium");
  const official = partners.filter((p) => p.level === "official");
  const media = partners.filter((p) => p.level === "media");

  return (
    <>
      <Seo title={tx.seo_title || "Partenaires — FACADES2build"} description={tx.seo_description || "Les acteurs engagés du secteur de la façade qui rendent FACADES2build possible."} />
      <section className="bg-f2b-black pt-40 pb-16">
        <div className="f2b-container">
          <Reveal><p className="f2b-kicker text-f2b-cyan mb-4">{tx.kicker || "Ils rendent l'événement possible"}</p></Reveal>
          <Reveal delay={0.08}><h1 className="font-display font-bold text-white text-4xl md:text-6xl leading-tight max-w-3xl">{tx.title || "Nos partenaires"}</h1></Reveal>
          <Reveal delay={0.16}><p className="text-white/70 mt-6 max-w-2xl">{tx.lead || "Des acteurs engagés du secteur de la façade, qui partagent avec nous l'ambition de faire progresser les pratiques."}</p></Reveal>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="f2b-container space-y-16">
          {isLoading ? (
            <p className="text-f2b-gray">Chargement…</p>
          ) : partners.length === 0 ? (
            <p className="text-f2b-gray" data-testid="partenaires-empty">Les partenaires de cette édition seront annoncés prochainement.</p>
          ) : (
            <>
              {premium.length > 0 && (
                <div data-testid="partners-premium">
                  <div className="grid gap-6">
                    {premium.map((p) => (
                      <Reveal key={p.id}>
                        <div className="relative bg-white border border-f2b-gold/40 p-10 md:p-12 flex flex-col md:flex-row md:items-center gap-8 md:gap-12" data-testid={`partner-${p.name}`}>
                          <span className="absolute top-0 left-0 bg-f2b-gold text-white text-[10px] tracking-[0.18em] uppercase px-3 py-1.5">Partenaire premium</span>
                          <div className="shrink-0 w-full md:w-72 h-28 md:h-32 flex items-center justify-center md:justify-start pt-4 md:pt-0">
                            {mediaUrl(p.logo) ? <img src={mediaUrl(p.logo)} alt={p.name} className="max-h-28 max-w-full object-contain" /> : <span className="font-display font-bold text-3xl text-f2b-black">{p.name}</span>}
                          </div>
                          <div className="flex-1">
                            <h3 className="font-display font-bold text-f2b-black text-2xl md:text-3xl leading-tight">{p.name}</h3>
                            {p.description && <p className="text-f2b-text/70 mt-4 leading-relaxed max-w-2xl">{p.description}</p>}
                            {p.website && (
                              <a href={p.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 mt-5 text-sm text-f2b-gray hover:text-f2b-gold transition-colors">
                                Site web <ExternalLink size={13} />
                              </a>
                            )}
                          </div>
                        </div>
                      </Reveal>
                    ))}
                  </div>
                </div>
              )}
              {official.length > 0 && (
                <div><h2 className="f2b-kicker text-f2b-cyan mb-6">Partenaires officiels</h2>
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">{official.map((p, i) => <Reveal key={p.id} delay={i * 0.05} className="h-full"><PartnerCard p={p} /></Reveal>)}</div>
                </div>
              )}
              {media.length > 0 && (
                <div><h2 className="f2b-kicker text-f2b-gray mb-6">Média partenaire</h2>
                  <div className="grid md:grid-cols-3 gap-6">{media.map((p) => <Reveal key={p.id} className="h-full"><PartnerCard p={p} /></Reveal>)}</div>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      <section className="py-16 bg-f2b-cyan">
        <div className="f2b-container flex flex-col md:flex-row items-center justify-between gap-6">
          <div>
            <h2 className="font-display font-bold text-f2b-black text-3xl">{tx.cta_title || "Devenir partenaire"}</h2>
            <p className="text-f2b-black/80 mt-2 max-w-xl">{tx.cta_text || "Associez votre marque à un événement reconnu pour la qualité de ses intervenants et la pertinence de ses contenus techniques."}</p>
          </div>
          <Link to="/devenir-partenaire"><Btn variant="dark" data-testid="cta-become-partner">{tx.cta_button || "Devenir partenaire"} <ArrowRight size={18} /></Btn></Link>
        </div>
      </section>
    </>
  );
};

export default Partenaires;
